import { WarningTwoIcon } from '@chakra-ui/icons';
import {
  Box,
  Center,
  Flex,
  Heading,
  Tab,
  TabIndicator,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useToast,
} from '@chakra-ui/react';
import { MotionBox } from '../../../components/motion/MotionComponents';
import { useReducedMotion } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import PageLoader from '../../../components/layout/page-loader/PageLoader';
import { usePermissions } from '../../../hooks/usePermissions';
import { postOfficeHoursList } from '../../../services/practices/postOfficeHoursList';
import { postUnifiedSearch } from '../../../services/unified-search/unified-search';
import { OfficeHour, Practice, Provider } from '../../../types';
import { analytics } from '../../../utils/analytics';
import {
  listItemVariants,
  searchStaggerContainer,
} from '../../../utils/motion';
import { PracticeItem } from '../practices/PracticeItem';
import ProviderItem from '../providers/ProviderItem';

interface GroupedOfficeHours {
  [practice_id: string]: OfficeHour[];
}
const UnifiedSearchResults: React.FC = () => {
  const navigate = useNavigate();
  const { q } = useParams();
  const toast = useToast();
  const prefersReducedMotion = useReducedMotion();
  const { permissions, isReady } = usePermissions();
  const [practice, setPractice] = useState<Practice[] | []>([]);
  const [provider, setProvider] = useState<Provider[] | []>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [officeHoursLoading, setOfficeHoursLoading] = useState(false);
  const [officeHours, setOfficeHours] = useState<GroupedOfficeHours>(
    {}
  );
  const fetchedOfficeHoursRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (q && isReady) {
      setLoading(true);
      postUnifiedSearch(q as string, 1000, undefined, undefined, permissions.isAdmin)
        .then((response) => {
          setPractice(response.data.practices);
          setProvider(response.data.providers);
          analytics.trackEvent('search', 'global', {
            queryLength: String(q || '').length,
            resultCount:
              (response.data.practices?.length || 0) +
              (response.data.providers?.length || 0),
          });
        })
        .catch((error: unknown) => {
          console.error(error);
          toast({
            title: 'Search Failed',
            description:
              'Failed to fetch search results. Please try again.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [q, toast, permissions.isAdmin, isReady]);

  const groupByPracticeId = useCallback(
    (response: {
      office_hours: OfficeHour[];
    }): GroupedOfficeHours => {
      return response.office_hours.reduce(
        (acc: GroupedOfficeHours, curr: OfficeHour) => {
          const key = curr.practice_id;
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(curr);
          return acc;
        },
        {}
      );
    },
    []
  );

  const fetchOfficeHours = useCallback(
    async (practiceIds: string[]) => {
      try {
        setOfficeHoursLoading(true);
        const data = await postOfficeHoursList(practiceIds);
        const groupedOfficeHours = groupByPracticeId(data.data);
        setOfficeHours(groupedOfficeHours);
        fetchedOfficeHoursRef.current = new Set([
          ...fetchedOfficeHoursRef.current,
          ...practiceIds,
        ]);
      } catch (error) {
        toast({
          title: 'Failed to fetch office hours',
          description: 'Please try again',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setOfficeHoursLoading(false);
      }
    },
    [groupByPracticeId, toast]
  );

  const updatePracticesWithOfficeHours = useCallback(() => {
    setPractice((prevItems: Practice[]) =>
      prevItems.map((practice) => ({
        ...practice,
        office_hours: officeHours[practice.id] || [],
      }))
    );
  }, [officeHours, setPractice]);

  // Update providers with office hours data for their related practices
  const updateProvidersWithOfficeHours = useCallback(() => {
    setProvider((prevItems: Provider[]) =>
      prevItems.map((provider) => ({
        ...provider,
        relatedPractices:
          provider.relatedPractices?.map((practice) => ({
            ...practice,
            office_hours: officeHours[practice.id] || [],
          })) || [],
      }))
    );
  }, [officeHours, setProvider]);

  // Get all practice IDs from both practices and providers
  const getAllPracticeIds = useCallback(() => {
    const practiceIds = practice.map((p) => p.id);
    const providerPracticeIds = provider
      .flatMap((p) => p.relatedPractices || [])
      .map((p) => p.id);

    // Combine and deduplicate
    return [...new Set([...practiceIds, ...providerPracticeIds])];
  }, [practice, provider]);

  useEffect(() => {
    const allPracticeIds = getAllPracticeIds();
    const newPracticeIds = allPracticeIds.filter(
      (id) => !fetchedOfficeHoursRef.current.has(id)
    );

    if (newPracticeIds.length > 0 && !officeHoursLoading) {
      fetchOfficeHours(newPracticeIds);
    }
  }, [
    practice,
    provider,
    officeHoursLoading,
    fetchOfficeHours,
    getAllPracticeIds,
  ]);

  useEffect(() => {
    if (Object.keys(officeHours).length > 0) {
      updatePracticesWithOfficeHours();
      updateProvidersWithOfficeHours();
    }
  }, [
    officeHours,
    updatePracticesWithOfficeHours,
    updateProvidersWithOfficeHours,
  ]);

  return (
    <>
      <Helmet>
        <title>
          {q && `${q} - Resource Matrix Search Results: `}
        </title>
      </Helmet>
      <MotionBox
        mt="2.31rem"
        mb="7.69rem"
        ml="2rem"
        mr="2rem"
        initial={
          prefersReducedMotion
            ? { opacity: 1, y: 0 }
            : { opacity: 0, y: 6 }
        }
        animate={{ opacity: 1, y: 0 }}
      >
        <Flex
          alignItems={'center'}
          justifyContent={'space-between'}
          mb="0.88rem"
          pb="1rem"
        >
          <Box>
            <Heading
              as="h1"
              size="md"
              color={'gray.600'}
              fontWeight={700}
              lineHeight={'1.5rem'}
            >
              Resource Matrix Search Results: {q}
            </Heading>
            <Text color="gray.500" fontSize="0.875rem" mt="0.5rem">
              Showing results for "{q}" • {practice.length} practices
              • {provider.length} providers
            </Text>
          </Box>
        </Flex>
        {!loading ? (
          <MotionBox
            background="gray.50"
            padding="1rem"
            mt="0.12rem"
            initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Tabs>
              <TabList>
                <Tab
                  p="0.25rem 0.75rem"
                  fontSize="0.75rem"
                  fontWeight="500"
                  lineHeight="1rem"
                >
                  Practices ({practice.length})
                </Tab>
                <Tab
                  p="0.25rem 0.75rem"
                  fontSize="0.75rem"
                  fontWeight="500"
                  lineHeight="1rem"
                >
                  Providers ({provider.length})
                </Tab>
              </TabList>
              <TabIndicator
                mt="-2px"
                height="2px"
                bg="background.brightLight"
              />
              <TabPanels>
                <TabPanel>
                  <MotionBox
                    display="flex"
                    flexDirection="column"
                    gap="0.75rem"
                    variants={
                      prefersReducedMotion
                        ? undefined
                        : searchStaggerContainer
                    }
                    initial={prefersReducedMotion ? undefined : 'initial'}
                    animate={prefersReducedMotion ? undefined : 'animate'}
                  >
                    {practice.length > 0 ? (
                      practice.map((practice) => (
                        <MotionBox
                          key={practice.id}
                          variants={
                            prefersReducedMotion
                              ? undefined
                              : listItemVariants
                          }
                        >
                          <PracticeItem
                            practice={practice}
                            onClick={() =>
                              navigate(`/practices/${practice.id}`)
                            }
                            officeHoursLoading={officeHoursLoading}
                          />
                        </MotionBox>
                      ))
                    ) : (
                      <Center>
                        <Box
                          borderWidth="1px"
                          borderRadius="lg"
                          boxShadow="sm"
                          p={[4, 6]}
                          textAlign={'center'}
                          bg="white"
                          mx="2"
                          maxW="md"
                        >
                          <WarningTwoIcon
                            boxSize={[8, 10]}
                            color="orange.400"
                            mb={4}
                          />
                          <Heading
                            size="lg"
                            mb={2}
                            color={'gray.800'}
                          >
                            No practices match your search
                          </Heading>
                          <Text mb={4} color={'gray.600'}>
                            Try adjusting your search terms or browse
                            all resources.
                          </Text>
                          <Text fontSize="0.75rem" color="gray.400">
                            Tip: Search by practice name, specialty,
                            market, or phone number
                          </Text>
                        </Box>
                      </Center>
                    )}
                  </MotionBox>
                </TabPanel>
                <TabPanel>
                  <MotionBox
                    display="flex"
                    flexDirection="column"
                    gap="0.75rem"
                    variants={
                      prefersReducedMotion
                        ? undefined
                        : searchStaggerContainer
                    }
                    initial={prefersReducedMotion ? undefined : 'initial'}
                    animate={prefersReducedMotion ? undefined : 'animate'}
                  >
                    {provider.length > 0 ? (
                      provider.map((provider) => (
                        <MotionBox
                          key={provider.id}
                          variants={
                            prefersReducedMotion
                              ? undefined
                              : listItemVariants
                          }
                        >
                          <ProviderItem
                            provider={provider}
                            officeHoursLoading={officeHoursLoading}
                          />
                        </MotionBox>
                      ))
                    ) : (
                      <Center>
                        <Box
                          borderWidth="1px"
                          borderRadius="lg"
                          boxShadow="sm"
                          p={[4, 6]}
                          textAlign={'center'}
                          bg="white"
                          mx="2"
                          maxW="md"
                        >
                          <WarningTwoIcon
                            boxSize={[8, 10]}
                            color="orange.400"
                            mb={4}
                          />
                          <Heading
                            size="lg"
                            mb={2}
                            color={'gray.800'}
                          >
                            No providers match your search
                          </Heading>
                          <Text mb={4} color={'gray.600'}>
                            Try adjusting your search terms or browse
                            all resources.
                          </Text>
                          <Text fontSize="0.75rem" color="gray.400">
                            Tip: Search by provider name, specialty,
                            market, or phone number
                          </Text>
                        </Box>
                      </Center>
                    )}
                  </MotionBox>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </MotionBox>
        ) : (
          <PageLoader height="45vh" />
        )}
      </MotionBox>
    </>
  );
};

export default UnifiedSearchResults;
